import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useRoute } from '../../context/RouteContext';
import {
  fetchTourRegistrations,
  registerFree,
  getMyRegistration,
  cancelTour,
  deleteTour,
  subscribeTour,
  reconcileRegistrationCount,
  getMyTourReview,
  fetchTourReviews,
  submitTourReview,
  regDocId,
  fetchLessonDateBookings,
  fetchLessonBookingsForDates,
} from '../../services/tourService';
import { createTourTicketSession, refundTour, trackTourView, cancelTourDate, cancelRegistrationSelf } from '../../services/stripeService';
import { track } from '../../services/analytics';
import { TicketQR } from './TicketQR';
import { BookingStatusModal } from './BookingStatusModal';
import { getProfile } from '../../services/profileService';
import { getUserStats } from '../../services/publishService';
import { formatDistance, formatDuration, bestDisplayName } from '../../utils/formatters';
import { ProfileLinks } from '../common/ProfileLinks';
import { BusinessOrganizerCard } from '../common/BusinessOrganizerCard';
import { MediaCarousel } from '../common/MediaCarousel';
import { Modal } from '../common/Modal';
import { LegalModal } from '../Legal/LegalModal';
import { INCLUSION_MAP } from './inclusions';
import { EmbedBuilder } from '../Embed/EmbedBuilder';
import { WeatherCard } from './WeatherCard';
import {
  isRecurring, formatRunsOn, nextOccurrence, fmtDateShort, isCheckInDay, isTourPast,
  intervalsOverlap, timeToMinutes as toMinutes, minutesToHM as fmtHM, buildLessonSlotOptions,
} from '../../utils/tourSchedule';
import './TourDetailModal.css';

const SKILL_COLORS = {
  beginner:     { color: '#16a34a', bg: '#f0fdf4', label: 'Beginner'     },
  intermediate: { color: '#d97706', bg: '#fffbeb', label: 'Intermediate' },
  expert:       { color: '#dc2626', bg: '#fef2f2', label: 'Expert'       },
};

// Buyer-paid booking fee — keep in sync with PLATFORM_FEE_PCT in functions/index.js
const PLATFORM_FEE_PCT = 0.05;

// Lazy to break the tour-detail ↔ user-profile import cycle.
const UserProfilePanel = lazy(() => import('../Profile/UserProfilePanel').then(m => ({ default: m.UserProfilePanel })));
// Lazy so the heavy html5-qrcode scanner only loads when an organizer opens it.
const CheckInScanner = lazy(() => import('./CheckInScanner').then(m => ({ default: m.CheckInScanner })));

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function spotsLabel(max, used) {
  if (!max) return `${used} registered`;
  const left = max - used;
  if (left <= 0) return 'Full';
  return `${left} of ${max} spots left`;
}

function isFull(max, used) {
  return max && used >= max;
}

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ymdToDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Next `n` dates a recurring tour runs on, starting today.
function listUpcoming(tour, n = 8) {
  const out = [];
  let from = new Date();
  for (let i = 0; i < n; i++) {
    const d = nextOccurrence(tour, from);
    if (!d) break;
    out.push(d);
    from = new Date(d);
    from.setDate(from.getDate() + 1);
  }
  return out;
}

// Small ring showing how full one occurrence is. Uncapped tours (no
// maxParticipants) just show the raw count with a neutral ring.
function SpotsRing({ used, cap }) {
  const r = 15, c = 2 * Math.PI * r;
  const pct = cap ? Math.min(1, used / cap) : 0;
  const full = cap != null && used >= cap;
  const color = !cap ? 'var(--color-text-muted)' : full ? '#dc2626' : pct >= 0.7 ? '#d97706' : '#16a34a';
  return (
    <svg width="38" height="38" viewBox="0 0 38 38" className="tdm__spots-ring" aria-hidden="true">
      <circle cx="19" cy="19" r={r} fill="none" stroke="var(--color-border)" strokeWidth="4" />
      {cap != null && (
        <circle
          cx="19" cy="19" r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round" transform="rotate(-90 19 19)"
        />
      )}
      <text x="19" y="23" textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>
        {cap != null ? (full ? '' : cap - used) : used}
      </text>
      {cap != null && full && (
        <path d="M14 19l3.5 3.5L25 15" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// Organizer promotion: shareable deep link + copy-paste HTML button.
function PromoteBox({ tour }) {
  const [copied, setCopied] = useState('');
  const origin = window.location.origin;
  const isPaid = tour.registrationType === 'paid';
  const url = `${origin}/tour/${tour.id}${isPaid ? '?buy=1' : ''}`;
  const label = isPaid
    ? `Buy tickets · $${(tour.ticketPrice * (1 + PLATFORM_FEE_PCT)).toFixed(2)}`
    : 'View & register';
  const embed =
    `<a href="${url}" target="_blank" rel="noopener" style="display:inline-block;` +
    `background:#6c5ce7;color:#fff;font-family:sans-serif;font-weight:700;font-size:15px;` +
    `padding:12px 22px;border-radius:12px;text-decoration:none;">${label} — ${tour.name}</a>`;

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="tdm__promote">
      <div className="tdm__promote-title">Promote this tour</div>
      <p className="tdm__promote-sub">
        Share a direct link, or embed a button on your site —
        {isPaid ? ' it takes buyers straight to checkout.' : ' it opens this tour in Motoroam.'}
      </p>
      <div className="tdm__promote-row">
        <input className="tdm__promote-input" readOnly value={url} onFocus={e => e.target.select()} />
        <button className="tdm__promote-btn" onClick={() => copy(url, 'url')}>
          {copied === 'url' ? 'Copied!' : 'Copy link'}
        </button>
      </div>
      <button className="tdm__promote-embed" onClick={() => copy(embed, 'embed')}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
        {copied === 'embed' ? 'HTML button copied!' : 'Copy HTML button'}
      </button>
    </div>
  );
}

export function TourDetailModal({ tour, onClose, onCancelled, onUpgrade, onEdit, onCountChange, autoBuy = false, zIndex = 1400 }) {
  const { user, signIn, loading: authLoading } = useAuth();
  const { dispatch, showToast } = useRoute();
  const [profileTarget, setProfileTarget] = useState(null);
  const autoBuyDone = useRef(false);

  const [activeTab,      setActiveTab]      = useState('info');
  const [myReg,          setMyReg]          = useState(null);
  const [regLoading,     setRegLoading]     = useState(false);
  const [cancelConfirm,  setCancelConfirm]  = useState(false);
  const [regs,           setRegs]           = useState([]);
  const [regsLoading,    setRegsLoading]    = useState(false);
  const [cancelLoading,  setCancelLoading]  = useState(false);
  const [regCount,       setRegCount]       = useState(tour.registrationCount || 0);
  const [regChecked,     setRegChecked]     = useState(false);
  const [organizerProfile, setOrganizerProfile] = useState(null);
  const [organizerStats, setOrganizerStats] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [bookingAgain, setBookingAgain] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateCounts, setDateCounts] = useState(tour.dateCounts || {});
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  const [viewCount, setViewCount] = useState(tour.viewCount || 0);
  const [viewsByDay, setViewsByDay] = useState(tour.viewsByDay || {});
  const [cancelledDates, setCancelledDates] = useState(tour.cancelledDates || []);
  const [cancelDateTarget, setCancelDateTarget] = useState(null); // YYYY-MM-DD pending confirm
  const [cancelDateLoading, setCancelDateLoading] = useState(false);

  // Lesson booking — a private, per-hour booking instead of a flat-price
  // registration. Hours/start time are chosen at booking time, not baked into
  // the listing, so the total is computed live here (and re-verified server-side).
  const [lessonHours,     setLessonHours]     = useState(2);
  const [lessonStartTime, setLessonStartTime] = useState(tour.availabilityStart || '09:00');
  const [lessonRentBike,  setLessonRentBike]  = useState(false);
  // { title, subtitle } while showing the post-booking confirmation dialog,
  // null otherwise — replaces a success toast, which is easy to miss.
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  // Message string while showing a booking-failed dialog (e.g. "you already
  // have a booking for that date") — a hard server rejection deserves more
  // attention than a toast that vanishes on its own.
  const [bookingError, setBookingError] = useState(null);
  // Risk/independent-operator disclaimer, required before the first booking
  // action on THIS tour — resets every time the modal is opened fresh.
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [showDisclaimerTerms, setShowDisclaimerTerms] = useState(false);
  // Other riders' confirmed bookings on the currently-selected date — a
  // coach's day fills up hour-by-hour, not all at once, so this drives both
  // the "already booked" hint and the overlap check before submit.
  const [dateBookings, setDateBookings] = useState([]);
  // Bookings for every date shown in the Availability tile — { ymd: [{startTime,hours}] }
  // — used only to say whether a day still has any open hour, never a headcount.
  const [upcomingBookings, setUpcomingBookings] = useState({});

  // Reviews
  const [avgRating, setAvgRating]     = useState(tour.averageRating ?? null);
  const [ratingCount, setRatingCount] = useState(tour.ratingCount ?? 0);
  const [reviews, setReviews]         = useState(null);
  const [myReview, setMyReview]       = useState(null);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText]   = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  const isOwner = user?.uid === tour.userId;
  const isLesson = tour.category === 'lesson';
  const recurring = isRecurring(tour);
  const todayYMD = toYMD(new Date());
  // viewsByDay is already pruned server-side to a rolling 7 days, so its
  // entries ARE "this week" — no further date filtering needed here.
  const viewsThisWeek = Object.values(viewsByDay).reduce((sum, n) => sum + n, 0);
  // cancelledDates changes during this modal's lifetime (cancelling a date
  // below) unlike the rest of the schedule fields, so it's tracked as live
  // state and merged in here rather than read straight off the `tour` prop.
  // Memoized so it's only a new reference when tour/cancelledDates actually
  // change — an unstable object here breaks the (also memoized)
  // upcomingRegGroups downstream and, with it, the tile expand/collapse state.
  const scheduleTour = useMemo(() => ({ ...tour, cancelledDates }), [tour, cancelledDates]);
  // Includes today — riders can still book today's ride if it hasn't run yet.
  const upcoming = recurring ? listUpcoming(scheduleTour, 8) : [];
  // Display-only, for the Upcoming Rides / Spots Left tiles — those are meant to
  // preview what's coming next, not repeat "today" info shown elsewhere on the page.
  const displayUpcoming = upcoming.filter(d => toYMD(d) !== todayYMD);
  // Lessons never use a per-date headcount cap — even on an older lesson doc
  // that still has maxParticipants:1 stored from before per-slot overlap
  // checking existed, always treat it as uncapped here.
  const perDateCap = isLesson ? 0 : (tour.maxParticipants || 0);
  const spotsForDate = ymd => (perDateCap ? perDateCap - (dateCounts[ymd] || 0) : null);
  const selectedFull = recurring && perDateCap > 0 && (dateCounts[selectedDate] || 0) >= perDateCap;
  const selectedSpots = recurring && selectedDate ? spotsForDate(selectedDate) : null;

  const registered = myReg?.status === 'confirmed';
  // The date THIS rider is booked on: their chosen occurrence for a recurring
  // tour, otherwise the tour's own start date.
  const myRideDate = (() => {
    if (recurring) {
      const d = myReg?.tourDate;
      if (!d) return null;
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    }
    return tour.startDate?.toDate ? tour.startDate.toDate() : (tour.startDate ? new Date(tour.startDate) : null);
  })();
  // Has the rider's booked ride already happened? Only then can they review.
  const rideDatePassed = (() => {
    if (!myRideDate) return false;
    const endOfDay = new Date(myRideDate);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay < new Date();
  })();
  const canReview = !isOwner && registered && rideDatePassed;
  // Preview only, for the cancel-confirm copy below — the server is
  // authoritative and recomputes this itself from trusted data. Keep in sync
  // with CANCEL_REFUND_CUTOFF_HOURS in functions/index.js.
  const cancelRefundEligible = myRideDate
    ? (myRideDate.getTime() - Date.now()) / 3_600_000 >= 48
    : false;
  // regs holds every registration ever made across every occurrence of a
  // recurring tour (each date is its own doc) — group by date so the owner
  // browses each upcoming ride separately, and so check-in only ever targets
  // riders actually booked on today's occurrence (not other weeks', including
  // the same person booked on several dates).
  const upcomingRegGroups = useMemo(() => {
    if (!recurring) return [];
    const byDate = new Map();
    for (const r of regs) {
      const d = r.tourDate;
      if (!d || d < todayYMD) continue; // past occurrences aren't actionable here
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(r);
    }
    // Today's tile needs to show even with zero registrations so far — the
    // organizer still needs it visible to check in walk-ins on a running day.
    if (isCheckInDay(scheduleTour) && !byDate.has(todayYMD)) byDate.set(todayYMD, []);
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, list]) => ({ date, regs: list, checkedIn: list.filter(r => r.checkedIn).length }));
  }, [regs, recurring, todayYMD, scheduleTour]); // eslint-disable-line react-hooks/exhaustive-deps
  const todayRegs = recurring ? regs.filter(r => r.tourDate === todayYMD) : regs;
  const checkedInCount = todayRegs.filter(r => r.checkedIn).length;
  // Tab badge: total booked across today + every future date, from dateCounts
  // (already live-synced via subscribeTour) rather than the lifetime total —
  // cheap since it needs no extra fetch of the full registrations list.
  const upcomingRegTotal = recurring
    ? Object.entries(dateCounts).filter(([d]) => d >= todayYMD).reduce((sum, [, c]) => sum + c, 0)
    : regCount;
  // Check-in is only allowed on the day the tour runs.
  const canCheckInToday = isCheckInDay(scheduleTour);
  const tourPast = isTourPast(scheduleTour);

  // ── Minimum riders ──
  // The tour only runs if enough people book. For recurring tours the minimum
  // applies PER DATE, so count that occurrence rather than the grand total.
  const minRiders = tour.minParticipants || 0;
  const minDateKey = recurring ? (registered && myReg?.tourDate ? myReg.tourDate : selectedDate) : null;
  const ridersToward = recurring ? (dateCounts[minDateKey] || 0) : regCount;
  const minMet = minRiders > 0 && ridersToward >= minRiders;
  const showMinProgress = minRiders > 0 && !tourPast && !rideDatePassed && tour.status !== 'cancelled';

  // Default the date picker to the next occurrence.
  useEffect(() => {
    if (recurring && !selectedDate && upcoming[0]) setSelectedDate(toYMD(upcoming[0]));
  }, [recurring]); // eslint-disable-line

  // Refresh the selected date's existing bookings whenever the date changes
  // (or right after this rider books, so the hint/overlap check stay current).
  useEffect(() => {
    if (!isLesson || !selectedDate) { setDateBookings([]); return; }
    let alive = true;
    fetchLessonDateBookings(tour.id, selectedDate)
      .then(list => { if (alive) setDateBookings(list); })
      .catch(() => { if (alive) setDateBookings([]); });
    return () => { alive = false; };
  }, [isLesson, tour.id, selectedDate, regCount]);

  // Bookings for the Availability tile's dates — one batched query rather
  // than one per row. Keyed on the actual date list (not the Date[] array
  // reference, which is a new object every render) so it only refetches when
  // the set of displayed days, or the booking count, actually changes.
  const upcomingYMDKey = displayUpcoming.map(toYMD).join(',');
  useEffect(() => {
    if (!isLesson || !upcomingYMDKey) { setUpcomingBookings({}); return; }
    let alive = true;
    fetchLessonBookingsForDates(tour.id, upcomingYMDKey.split(','))
      .then(map => { if (alive) setUpcomingBookings(map); })
      .catch(() => { if (alive) setUpcomingBookings({}); });
    return () => { alive = false; };
  }, [isLesson, tour.id, upcomingYMDKey, regCount]);

  const dateSelectEl = recurring && upcoming.length > 0 && (
    <div className="tdm__date-select">
      <label className="tdm__date-select-label">{isLesson ? 'Choose a day' : 'Choose your date'}</label>
      <select className="tdm__date-select-input" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}>
        {upcoming.map(d => {
          const v = toYMD(d);
          const left = spotsForDate(v);
          const full = left != null && left <= 0;
          return (
            <option key={v} value={v} disabled={full}>
              {fmtDateShort(d)}
              {!isLesson && left != null ? (full ? ' — Full' : ` — ${left} left`) : ''}
            </option>
          );
        })}
      </select>
      {!isLesson && selectedSpots != null && !selectedFull && selectedSpots <= 5 && (
        <span className="tdm__date-spots">Only {selectedSpots} {selectedSpots === 1 ? 'spot' : 'spots'} left on this date</span>
      )}
      {selectedFull && (
        <span className="tdm__date-spots tdm__date-spots--full">This date is full — pick another</span>
      )}
    </div>
  );

  // ── Lesson booking price/window (private, per-hour) ──
  const lessonWinStart = tour.availabilityStart || '00:00';
  const lessonWinEnd   = tour.availabilityEnd   || '23:59';
  // Is there any free gap of at least `minMinutes` left in the window, given
  // the day's existing bookings? Used for the Availability tile's plain
  // Available/Fully booked label — never a headcount.
  const hasOpenSlot = (bookings, winStartMin, winEndMin, minMinutes = 60) => {
    const sorted = bookings
      .map(b => { const s = toMinutes(b.startTime); return [s, s + Math.round((b.hours || 0) * 60)]; })
      .sort((a, b) => a[0] - b[0]);
    let cursor = winStartMin;
    for (const [s, e] of sorted) {
      if (s - cursor >= minMinutes) return true;
      cursor = Math.max(cursor, e);
    }
    return winEndMin - cursor >= minMinutes;
  };

  // Every bookable start time for the chosen duration — stepped every 30 min
  // across the availability window, each flagged if it would overlap another
  // confirmed booking (including the rider's own, if they're re-opening this
  // to book a different date). Replaces a free-text time input: the rider
  // picks from what's actually available instead of guessing and getting
  // rejected. See utils/tourSchedule.test.js for the conflict-detection tests.
  const lessonStartOptions = isLesson
    ? buildLessonSlotOptions({ winStart: lessonWinStart, winEnd: lessonWinEnd, hours: lessonHours, bookings: dateBookings })
    : [];
  const lessonSelectedOption = lessonStartOptions.find(o => o.value === lessonStartTime);
  const lessonSlotOk = !!lessonSelectedOption && !lessonSelectedOption.disabled;

  // Keep the selected start time valid as the day/hours/existing bookings
  // change — snap to the first still-available option rather than leaving a
  // stale or now-booked time selected.
  useEffect(() => {
    if (!isLesson) return;
    if (lessonSelectedOption && !lessonSelectedOption.disabled) return;
    const firstFree = lessonStartOptions.find(o => !o.disabled);
    if (firstFree) setLessonStartTime(firstFree.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLesson, selectedDate, lessonHours, dateBookings, lessonWinStart, lessonWinEnd]);

  const lessonWantsBike = isLesson && lessonRentBike && tour.rentBikeAvailable;
  const lessonBaseCost  = isLesson ? (Number(tour.hourlyRate) || 0) * lessonHours : 0;
  const lessonBikeCost  = lessonWantsBike ? (Number(tour.bikeHourlyRate) || 0) * lessonHours : 0;
  const lessonSubtotal  = lessonBaseCost + lessonBikeCost;
  const lessonFee       = lessonSubtotal * PLATFORM_FEE_PCT;
  // A free lesson still needs checkout if the rider opts into paid bike
  // rental — only a fully-free booking (no bike, or bike not offered) skips it.
  const needsCheckout   = tour.registrationType === 'paid' || lessonWantsBike;

  const lessonBookingFieldsEl = isLesson && (
    <div className="tdm__lesson-booking">
      <div className="tdm__lesson-row">
        <label className="tdm__lesson-field">
          <span className="tdm__lesson-field-label">Hours</span>
          <input
            type="number"
            className="tdm__lesson-hours"
            min="1"
            max="12"
            step="1"
            value={lessonHours}
            onChange={e => setLessonHours(Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1)))}
          />
        </label>
        <label className="tdm__lesson-field">
          <span className="tdm__lesson-field-label">Time</span>
          {lessonStartOptions.length > 0 ? (
            <select
              className="tdm__lesson-time"
              value={lessonStartTime}
              onChange={e => setLessonStartTime(e.target.value)}
            >
              {lessonStartOptions.map(o => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}{o.disabled ? ' — booked' : ''}
                </option>
              ))}
            </select>
          ) : (
            <select className="tdm__lesson-time" disabled><option>No times available</option></select>
          )}
        </label>
      </div>
      {lessonStartOptions.length === 0 && (
        <span className="tdm__date-spots tdm__date-spots--full">
          No {lessonHours}h slot fits this day’s availability — try fewer hours or another day.
        </span>
      )}
      {tour.rentBikeAvailable && (
        <label className="tdm__check-row">
          <input type="checkbox" checked={lessonRentBike} onChange={e => setLessonRentBike(e.target.checked)} />
          <span>Hire a bike (+${Number(tour.bikeHourlyRate || 0).toFixed(2)}/hr)</span>
        </label>
      )}
      {/* Live total — recomputes on every hours/bike-rental change. Shown
          whenever there's actually something to pay for: a paid lesson, or a
          free one where the rider opted into paid bike rental. */}
      {(tour.registrationType === 'paid' || lessonWantsBike) && (
        <div className="tdm__lesson-total">
          <span>
            {tour.registrationType === 'paid' && `${lessonHours}h × $${Number(tour.hourlyRate || 0).toFixed(2)}/hr`}
            {tour.registrationType === 'paid' && lessonWantsBike && ' + '}
            {lessonWantsBike && 'bike hire'}
          </span>
          <strong>${lessonSubtotal.toFixed(2)}</strong>
        </div>
      )}
    </div>
  );

  // Independent-operator / risk disclaimer, required before the first booking
  // action — Motoroam is a booking platform, not the tour/lesson operator.
  const disclaimerEl = (
    <label className="tdm__disclaimer">
      <input type="checkbox" checked={acceptedDisclaimer} onChange={e => setAcceptedDisclaimer(e.target.checked)} />
      <span>
        I understand {isLesson ? 'this lesson is' : 'this tour is'} organized independently by {tour.userName} — not
        Motoroam — and I accept the risks of this activity.{' '}
        <button type="button" className="tdm__disclaimer-link" onClick={() => setShowDisclaimerTerms(true)}>Terms</button>
      </span>
    </label>
  );

  useEffect(() => {
    getProfile(tour.userId).then(setOrganizerProfile).catch(() => {});
    getUserStats(tour.userId).then(setOrganizerStats).catch(() => {});
  }, [tour.userId]);

  // View counter — once per browser per tour per day, and never for the
  // organizer's own views. Waits for auth to resolve first so a signed-in
  // owner whose session hasn't hydrated yet doesn't get counted as a viewer.
  // Failures are swallowed — a missed view should never surface to the viewer.
  useEffect(() => {
    if (authLoading || isOwner) return;
    const key = `mtr_viewed_${tour.id}_${todayYMD}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    trackTourView(tour.id).catch(() => {});
  }, [authLoading, isOwner, tour.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const skill   = SKILL_COLORS[tour.skillLevel] ?? SKILL_COLORS.intermediate;
  const hasImgs = tour.imageUrls?.length > 0;

  // Live registration count from Firestore — also propagate up so the
  // list card stays in sync with the real value.
  useEffect(() => {
    const unsub = subscribeTour(tour.id, live => {
      const c = live.registrationCount || 0;
      setRegCount(c);
      setDateCounts(live.dateCounts || {});
      setAvgRating(live.averageRating ?? null);
      setRatingCount(live.ratingCount ?? 0);
      setViewCount(live.viewCount || 0);
      setViewsByDay(live.viewsByDay || {});
      setCancelledDates(live.cancelledDates || []);
      onCountChange?.(tour.id, c);
    });
    return unsub;
  }, [tour.id, onCountChange]);

  // Check if current user is already registered
  useEffect(() => {
    if (!user) { setRegChecked(false); return; }
    setRegChecked(false);
    getMyRegistration(tour.id, user.uid)
      .then(setMyReg)
      .catch(() => {})
      .finally(() => setRegChecked(true));
  }, [tour.id, user]);

  // Load reviews + the user's own review.
  useEffect(() => {
    fetchTourReviews(tour.id).then(setReviews).catch(() => setReviews([]));
  }, [tour.id]);

  useEffect(() => {
    if (!user) { setMyReview(null); return; }
    getMyTourReview(tour.id, user.uid).then(r => {
      setMyReview(r);
      if (r) { setReviewStars(r.stars); setReviewText(r.text || ''); }
    }).catch(() => {});
  }, [tour.id, user]);

  // Deep-link "buy" intent: once signed in and we know the user isn't already
  // registered, jump straight to Stripe checkout (matches the Buy button).
  useEffect(() => {
    if (!autoBuy || autoBuyDone.current) return;
    if (!user || !regChecked) return;
    if (tour.registrationType !== 'paid') return;
    if (myReg?.status === 'confirmed') return;
    if (!recurring && isFull(tour.maxParticipants, regCount)) return;
    autoBuyDone.current = true;
    handleBuyTicket();
  }, [autoBuy, user, regChecked, myReg, regCount]); // eslint-disable-line

  // Load registrations when owner switches to the tab
  const loadRegistrations = useCallback(async () => {
    setRegsLoading(true);
    try {
      const list = await fetchTourRegistrations(tour.id);
      setRegs(list);
      // Heal any stale count left over from before the server-side trigger.
      if (isOwner) {
        setRegCount(prev => {
          if (prev !== list.length) {
            reconcileRegistrationCount(tour.id, list.length).catch(() => {});
          }
          return list.length;
        });
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRegsLoading(false);
    }
  }, [tour.id, isOwner, showToast]);

  useEffect(() => {
    if (activeTab === 'registrations' && isOwner) loadRegistrations();
  }, [activeTab, isOwner, loadRegistrations]);

  // Open the nearest upcoming date's tile by default (usually today's), without
  // clobbering the owner's own expand/collapse choices on later refreshes.
  useEffect(() => {
    if (expandedDates.size > 0 || upcomingRegGroups.length === 0) return;
    setExpandedDates(new Set([upcomingRegGroups[0].date]));
  }, [upcomingRegGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDateExpanded = date => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  // Close on Escape
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleRegisterFree() {
    if (!user) { showToast('Sign in to register', 'info'); return; }
    if (!acceptedDisclaimer) { showToast('Please accept the disclaimer first', 'info'); return; }
    if (recurring && !selectedDate) { showToast('Choose a date first', 'info'); return; }
    if (isLesson && !lessonSlotOk) { showToast('That time is no longer available — pick another', 'error'); return; }
    setRegLoading(true);
    try {
      const tourDate = recurring ? selectedDate : null;
      await registerFree(tour.id, user, tourDate, isLesson ? { hours: lessonHours, startTime: lessonStartTime } : null);
      setMyReg({
        id: regDocId(user.uid, tourDate),
        status: 'confirmed', paymentStatus: 'free', tourDate, checkedIn: false,
        ...(isLesson ? { lessonHours, lessonStartTime, lessonRentBike: false } : {}),
      });
      setBookingAgain(false);
      setRegCount(c => c + 1);
      if (tourDate) setDateCounts(dc => ({ ...dc, [tourDate]: (dc[tourDate] || 0) + 1 }));
      setConfirmedBooking({
        title: isLesson ? 'Lesson booked!' : "You're registered!",
        subtitle: isLesson
          ? `${fmtDateShort(tourDate)} at ${lessonStartTime} · ${lessonHours}h with ${tour.userName}.`
          : (tourDate ? `You're in for ${fmtDateShort(tourDate)}. Check your email for confirmation.` : 'Check your email for confirmation.'),
      });
    } catch (err) {
      setBookingError(err.message);
    } finally {
      setRegLoading(false);
    }
  }

  async function handleBuyTicket() {
    if (!user) { showToast('Sign in to buy a ticket', 'info'); return; }
    if (!acceptedDisclaimer) { showToast('Please accept the disclaimer first', 'info'); return; }
    if (recurring && !selectedDate) { showToast('Choose a date first', 'info'); return; }
    if (isLesson && !lessonSlotOk) { showToast('That time is no longer available — pick another', 'error'); return; }
    setRegLoading(true);
    try {
      const url = await createTourTicketSession(
        tour.id,
        recurring ? selectedDate : null,
        isLesson ? { hours: lessonHours, startTime: lessonStartTime, rentBike: lessonRentBike } : null,
      );
      window.location.href = url;
    } catch (err) {
      setBookingError(err.message);
      setRegLoading(false);
    }
  }

  async function handleSubmitReview() {
    if (!user || !reviewStars) { showToast('Pick a star rating first', 'info'); return; }
    setReviewSaving(true);
    try {
      await submitTourReview(tour.id, user, { stars: reviewStars, text: reviewText.trim() });
      const saved = {
        stars: reviewStars, text: reviewText.trim(),
        userId: user.uid, userName: bestDisplayName(user), userPhoto: user.photoURL || null,
        createdAt: { toDate: () => new Date() },
      };
      const had = !!myReview;
      setMyReview(saved);
      setReviews(list => {
        const others = (list || []).filter(r => r.id !== user.uid);
        return [{ id: user.uid, ...saved }, ...others];
      });
      // Optimistic aggregate update.
      setRatingCount(c => had ? c : c + 1);
      setAvgRating(prev => {
        const total = (prev ?? 0) * ratingCount - (myReview?.stars ?? 0) + reviewStars;
        const count = had ? ratingCount : ratingCount + 1;
        return count ? total / count : reviewStars;
      });
      showToast(had ? 'Review updated' : 'Thanks for your review!', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setReviewSaving(false);
    }
  }

  async function handleCancelRegistration() {
    if (!user || !myReg) return;
    setRegLoading(true);
    try {
      const result = await cancelRegistrationSelf(tour.id, myReg?.id || user.uid);
      setMyReg(null);
      setRegCount(c => Math.max(0, c - 1));
      setCancelConfirm(false);
      // Refresh the registrations list if it was already loaded
      if (regs.length > 0) loadRegistrations();
      if (result.refunded) {
        showToast('Registration cancelled — your ticket has been refunded.', 'info');
      } else if (result.refundError) {
        showToast('Registration cancelled. Your refund needs manual follow-up — contact support.', 'error');
      } else if (result.refundEligible === false && myReg.paymentStatus === 'paid') {
        showToast('Registration cancelled. Cancellations within 48 hours of the ride aren’t refunded.', 'info');
      } else {
        showToast('Registration cancelled', 'info');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRegLoading(false);
    }
  }

  async function handleCancelDate() {
    if (!cancelDateTarget) return;
    setCancelDateLoading(true);
    try {
      await cancelTourDate(tour.id, cancelDateTarget);
      setCancelledDates(prev => [...prev, cancelDateTarget]);
      showToast(`${fmtDateShort(ymdToDate(cancelDateTarget))} cancelled`, 'info');
      setCancelDateTarget(null);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCancelDateLoading(false);
    }
  }

  // Shared roster row — used both by the flat one-off list and each expandable
  // per-date group on a recurring tour. Check-in only ever happens by scanning
  // the rider's ticket QR (see the "Scan check-in" button) — there's no manual
  // organizer override, so a rider can't be marked present without actually
  // being there to present their ticket.
  function renderRegRow(reg, i) {
    return (
      <li key={reg.id} className="tdm__reg-row">
        <span className="tdm__reg-num">{i + 1}</span>
        {reg.userPhoto
          ? <img src={reg.userPhoto} alt="" className="tdm__reg-avatar" />
          : <span className="tdm__reg-avatar tdm__reg-avatar--initial">{reg.userName?.[0]?.toUpperCase() ?? '?'}</span>
        }
        <div className="tdm__reg-info">
          <span className="tdm__reg-name">{reg.userName}</span>
          <span className="tdm__reg-email">{reg.userEmail}</span>
          {reg.lessonStartTime && (
            <span className="tdm__reg-lesson-meta">
              {reg.lessonStartTime} · {reg.lessonHours}h{reg.lessonRentBike ? ' · Bike hire' : ''}
            </span>
          )}
        </div>
        {reg.checkedIn && (
          <span className="tdm__reg-checkedin" title="Checked in">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          </span>
        )}
        <span className={`tdm__reg-badge${reg.paymentStatus === 'paid' ? ' tdm__reg-badge--paid' : ''}`}>
          {reg.paymentStatus === 'paid' ? 'Paid' : 'Free'}
        </span>
      </li>
    );
  }

  async function handleCancelTour() {
    setCancelLoading(true);
    try {
      if (tour.registrationType === 'paid') {
        // Paid tours: cancel + refund every rider via the connected account.
        const r = await refundTour(tour.id);
        showToast(
          r.failed > 0
            ? `Tour cancelled. ${r.refunded} refunded, ${r.failed} need manual review.`
            : `Tour cancelled${r.refunded ? ` · ${r.refunded} refunded` : ''}`,
          r.failed > 0 ? 'error' : 'info',
        );
      } else {
        await cancelTour(tour.id);
        showToast('Tour cancelled', 'info');
      }
      setShowCancelConfirm(false);
      onCancelled?.(tour.id);
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
      setCancelLoading(false);
    }
  }

  async function handleDeleteTour() {
    setCancelLoading(true);
    try {
      await deleteTour(tour.id);
      showToast('Tour deleted', 'info');
      setShowDeleteConfirm(false);
      onCancelled?.(tour.id);
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
      setCancelLoading(false);
    }
  }

  function handleLoad() {
    if (!tour.waypoints?.length) return;
    dispatch({
      type: 'LOAD_ROUTE',
      payload: {
        id:          'tour_' + tour.id,
        name:        tour.name,
        waypoints:   tour.waypoints,
        geometry:    tour.geometry,
        distance:    tour.distance,
        duration:    tour.duration,
        communityMeta: {
          id:          tour.id,
          name:        tour.name,
          description: tour.description,
          imageUrls:   tour.imageUrls ?? [],
          videoUrls:   [],
          userName:    tour.userName,
          userPhoto:   tour.userPhoto,
        },
      },
    });
    track('tour_loaded', { tour_id: tour.id });
    showToast('Tour route loaded', 'success');
    onClose();
  }

  return (<>
    {profileTarget && (
      <Suspense fallback={null}>
        <UserProfilePanel
          uid={profileTarget.uid}
          fallbackName={profileTarget.name}
          fallbackPhoto={profileTarget.photo}
          zIndex={zIndex + 10}
          onClose={() => setProfileTarget(null)}
        />
      </Suspense>
    )}
    {createPortal(
    <div className="tdm-overlay" style={{ zIndex }} onClick={e => e.target.classList.contains('tdm-overlay') && onClose()}>
      <aside className="tdm-panel">

        {/* Header */}
        <div className="tdm__header">
          <button className="tdm__close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>

        {/* Banner image or gradient */}
        <div className="tdm__banner">
          {hasImgs ? (
            <MediaCarousel
              className="mc--fill"
              items={tour.imageUrls.map(src => ({ type: 'image', src }))}
            />
          ) : (
            <div className="tdm__banner-gradient">
              <span className="tdm__banner-letter">{tour.name[0]?.toUpperCase()}</span>
            </div>
          )}

          {/* Overlaid badges */}
          <div className="tdm__banner-badges">
            {tour.category === 'lesson' && (
              <span className="tdm__badge" style={{ background: '#d97706', color: '#fff' }}>
                {tour.learnerFriendly ? 'Lesson · Learner-friendly' : 'Lesson'}
              </span>
            )}
            {tour.category !== 'lesson' && (
              <span className="tdm__badge" style={{ background: skill.bg, color: skill.color }}>{skill.label}</span>
            )}
            <span className={`tdm__badge tdm__badge--reg${tour.registrationType === 'paid' ? ' tdm__badge--paid' : ''}`}>
              {tour.registrationType === 'paid'
                ? (isLesson ? `$${Number(tour.hourlyRate || 0).toFixed(2)}/hr` : `$${tour.ticketPrice?.toFixed(2)}`)
                : 'Free'}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="tdm__body">

          {/* Title */}
          <div className="tdm__title-block">
            <h2 className="tdm__name">
              {tour.name}
              {tour.tourCode && <span className="tdm__code">{tour.tourCode}</span>}
            </h2>
            {(tour.startName || tour.country) && (
              <div className="tdm__location">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                {[tour.startName, tour.region, tour.country].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>

          {/* Organizer profile (clickable) */}
          <div className="tdm__profile">
            <button
              className="tdm__profile-main"
              onClick={() => setProfileTarget({ uid: tour.userId, name: tour.userName, photo: tour.userPhoto })}
            >
              {tour.userPhoto
                ? <img src={tour.userPhoto} alt="" className="tdm__profile-avatar" />
                : <span className="tdm__profile-avatar tdm__profile-avatar--initial">{tour.userName?.[0]?.toUpperCase() ?? '?'}</span>}
              <span className="tdm__profile-info">
                <span className="tdm__profile-name">{tour.userName}</span>
                {organizerStats && (
                  <span className="tdm__profile-stats">
                    {organizerStats.totalTours > 0 && <>{organizerStats.totalTours} {organizerStats.totalTours === 1 ? 'tour' : 'tours'} hosted · </>}
                    {organizerStats.totalRoutes} {organizerStats.totalRoutes === 1 ? 'route' : 'routes'}
                    {organizerStats.avgRating != null && <> · <span className="tdm__profile-star">★</span> {organizerStats.avgRating.toFixed(1)} avg</>}
                  </span>
                )}
              </span>
              <svg className="tdm__profile-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            {organizerProfile && (organizerProfile.isBusiness || organizerProfile.links?.length > 0) && (
              <div className="tdm__profile-links"><ProfileLinks profile={organizerProfile} hideBusiness /></div>
            )}
          </div>

          <BusinessOrganizerCard business={tour.business} />

          {/* Tabs (only show Registrations tab to owner) */}
          {isOwner && (
            <div className="tdm__tabs">
              <button
                className={`tdm__tab${activeTab === 'info' ? ' tdm__tab--active' : ''}`}
                onClick={() => setActiveTab('info')}
              >Details</button>
              <button
                className={`tdm__tab${activeTab === 'registrations' ? ' tdm__tab--active' : ''}`}
                onClick={() => setActiveTab('registrations')}
              >
                Registrations
                {upcomingRegTotal > 0 && (
                  <span className="tdm__tab-count">{upcomingRegTotal}</span>
                )}
              </button>
            </div>
          )}

          {/* ── Info tab ── */}
          {activeTab === 'info' && (
            <>
              {/* Stats grid */}
              <div className="tdm__stats-grid">
                {/* Recurring + not registered: schedule/next-date is now covered
                    by the full-width Upcoming Rides tile below, so this stat is
                    only shown when it has rider-specific info (their booked date)
                    or the tour isn't recurring at all. */}
                {(!recurring || (registered && myRideDate)) && (
                  <div className="tdm__stat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <div>
                      {recurring ? (
                        <>
                          <div className="tdm__stat-val">{fmtDateShort(myRideDate)}</div>
                          <div className="tdm__stat-key">
                            {isLesson && myReg?.lessonStartTime
                              ? `${myReg.lessonStartTime} · ${myReg.lessonHours}h`
                              : tour.departTime ? `Departs ${tour.departTime}` : 'Your booked date'}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="tdm__stat-val">{fmtDate(tour.startDate)}</div>
                          <div className="tdm__stat-key">{fmtTime(tour.startDate)}{tour.endDate && ` – ${fmtTime(tour.endDate)}`}</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* Recurring + capped: per-date capacity is now covered by the
                    Spots Left tile below. Uncapped recurring tours have no
                    per-date alternative, so keep the lifetime total here. */}
                {!(recurring && perDateCap > 0) && (
                  <div className="tdm__stat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    <div>
                      <div className="tdm__stat-val">{spotsLabel(tour.maxParticipants, regCount)}</div>
                      <div className="tdm__stat-key">Participants</div>
                    </div>
                  </div>
                )}
                {/* Owner-only — a viewer doesn't need to see their own view counted. */}
                {isOwner && viewCount > 0 && (
                  <div className="tdm__stat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>
                    <div>
                      <div className="tdm__stat-val">{viewCount} {viewCount === 1 ? 'view' : 'views'}</div>
                      <div className="tdm__stat-key">{viewsThisWeek} this week</div>
                    </div>
                  </div>
                )}
                {tour.distance != null && (
                  <div className="tdm__stat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/></svg>
                    <div>
                      <div className="tdm__stat-val">{formatDistance(tour.distance)}</div>
                      <div className="tdm__stat-key">Distance</div>
                    </div>
                  </div>
                )}
                {tour.duration != null && (
                  <div className="tdm__stat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    <div>
                      <div className="tdm__stat-val">{formatDuration(tour.duration)}</div>
                      <div className="tdm__stat-key">Est. time</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Upcoming rides — date + spots-left in one place. Tap a row to
                  browse that date (drives the date picker + weather card below).
                  Starts tomorrow — today's own info is already covered elsewhere
                  on this tab. A lesson is exclusive per rider (no "book another
                  date"), so once booked there's nothing left to do with this
                  list — the "You're registered" section already shows their
                  own slot, so the full list would just be noise. */}
              {recurring && !tourPast && displayUpcoming.length > 0 && !(isLesson && registered) && (
                <div className="tdm__spots-tile">
                  <div className="tdm__tile-head">
                    <span className="tdm__tile-title">{isLesson ? 'Availability' : 'Upcoming Rides'}</span>
                    <span className="tdm__tile-sub">{formatRunsOn(tour.runsOn)}</span>
                  </div>
                  <ul className="tdm__spots-list">
                    {displayUpcoming.slice(0, 6).map((d, i) => {
                      const v = toYMD(d);
                      const used = dateCounts[v] || 0;
                      const left = spotsForDate(v);
                      // The rider's own booked day on this lesson gets a green
                      // checkmark + their actual time, not a generic status —
                      // and never "N booked" for anyone, just whether an hour
                      // is still open.
                      const isMine = isLesson && registered && myReg?.tourDate === v;
                      const dayOpen = isLesson
                        ? hasOpenSlot(upcomingBookings[v] || [], toMinutes(lessonWinStart), toMinutes(lessonWinEnd))
                        : null;
                      const full = isLesson ? (!dayOpen && !isMine) : (left != null && left <= 0);
                      const active = v === selectedDate;
                      const myEnd = isMine
                        ? fmtHM(toMinutes(myReg.lessonStartTime) + Math.round((myReg.lessonHours || 0) * 60))
                        : null;
                      return (
                        <li key={v}>
                          <div className={`tdm__spots-row${active ? ' tdm__spots-row--active' : ''}${full ? ' tdm__spots-row--full' : ''}${isMine ? ' tdm__spots-row--mine' : ''}`}>
                            <button
                              className="tdm__spots-row-main"
                              onClick={() => setSelectedDate(v)}
                              disabled={full && !isMine}
                            >
                              {isLesson ? (
                                <span className={`tdm__lesson-day-icon${isMine ? ' tdm__lesson-day-icon--mine' : full ? ' tdm__lesson-day-icon--full' : ''}`}>
                                  {isMine ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                  ) : full ? (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  ) : (
                                    <svg width="9" height="9" viewBox="0 0 9 9"><circle cx="4.5" cy="4.5" r="4.5" fill="currentColor"/></svg>
                                  )}
                                </span>
                              ) : (
                                <SpotsRing used={used} cap={perDateCap || null} />
                              )}
                              <div className="tdm__spots-row-info">
                                <span className="tdm__spots-row-date">
                                  {fmtDateShort(d)}
                                  {i === 0 && <span className="tdm__spots-row-tag">Next</span>}
                                </span>
                                <span className="tdm__spots-row-count">
                                  {isLesson
                                    ? (isMine ? `Booked ${myReg.lessonStartTime}–${myEnd}` : (dayOpen ? 'Available' : 'Fully booked'))
                                    : (full ? 'Full' : perDateCap ? `${used}/${perDateCap} booked` : `${used} registered`)}
                                </span>
                              </div>
                            </button>
                            {/* Cancelling is only offered while the date has no
                                registrations — once someone's booked, use Cancel
                                Tour (which refunds) instead of this quick action. */}
                            {isOwner && used === 0 && (
                              <button
                                className="tdm__spots-row-cancel"
                                onClick={() => setCancelDateTarget(v)}
                                title="Cancel this date"
                                aria-label={`Cancel ${fmtDateShort(d)}`}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {cancelDateTarget && (
                <Modal
                  title="Cancel this date?"
                  onClose={() => !cancelDateLoading && setCancelDateTarget(null)}
                  onConfirm={handleCancelDate}
                  confirmLabel={cancelDateLoading ? 'Cancelling…' : 'Cancel date'}
                  confirmVariant="danger"
                  confirmDisabled={cancelDateLoading}
                >
                  <p className="tdm__cancel-date-text">
                    {fmtDateShort(ymdToDate(cancelDateTarget))} will stop being bookable.
                    Every other upcoming date for this tour is unaffected.
                  </p>
                </Modal>
              )}

              {/* Meeting point */}
              {tour.meetingPoint && (
                <div className="tdm__meeting">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                  <div className="tdm__meeting-info">
                    <span className="tdm__meeting-label">Meeting point</span>
                    <span className="tdm__meeting-name">{tour.meetingPoint.name}</span>
                  </div>
                  <a
                    className="tdm__directions-btn"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${
                      tour.meetingPoint.lat != null && tour.meetingPoint.lng != null
                        ? `${tour.meetingPoint.lat},${tour.meetingPoint.lng}`
                        : encodeURIComponent(tour.meetingPoint.name)
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open directions in Google Maps"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                    Directions
                  </a>
                </div>
              )}

              {/* Weather forecast — for the rider's own booked date once
                  registered, otherwise the date they're browsing. Hidden once
                  that ride is in the past (a forecast for it is meaningless). */}
              {!rideDatePassed && !tourPast && (
                <WeatherCard
                  lat={tour.meetingPoint?.lat ?? tour.startLat ?? tour.waypoints?.find(w => w.lat != null)?.lat}
                  lng={tour.meetingPoint?.lng ?? tour.startLng ?? tour.waypoints?.find(w => w.lat != null)?.lng}
                  date={
                    registered && myRideDate
                      ? myRideDate
                      : recurring
                        ? (selectedDate ? new Date(selectedDate) : nextOccurrence(scheduleTour))
                        : tour.startDate
                  }
                />
              )}

              {/* Description */}
              {tour.description && (
                <div
                  className="tdm__description"
                  dangerouslySetInnerHTML={{ __html: tour.description }}
                />
              )}

              {/* Quick facts */}
              {isLesson ? (
                (tour.availabilityStart || tour.rentBikeAvailable) && (
                  <div className="tdm__facts">
                    {tour.availabilityStart && (
                      <div className="tdm__fact">
                        <span className="tdm__fact-k">Availability</span>
                        <span className="tdm__fact-v">{tour.availabilityStart}–{tour.availabilityEnd}</span>
                      </div>
                    )}
                    {tour.rentBikeAvailable && (
                      <div className="tdm__fact">
                        <span className="tdm__fact-k">Bike hire</span>
                        <span className="tdm__fact-v">${Number(tour.bikeHourlyRate || 0).toFixed(2)}/hr</span>
                      </div>
                    )}
                  </div>
                )
              ) : (tour.departTime || tour.durationLabel || tour.securityBond > 0) && (
                <div className="tdm__facts">
                  {(tour.departTime || tour.returnTime) && (
                    <div className="tdm__fact">
                      <span className="tdm__fact-k">Departs</span>
                      <span className="tdm__fact-v">{tour.departTime}{tour.returnTime ? ` · returns ~${tour.returnTime}` : ''}</span>
                    </div>
                  )}
                  {tour.durationLabel && (
                    <div className="tdm__fact"><span className="tdm__fact-k">Duration</span><span className="tdm__fact-v">{tour.durationLabel}</span></div>
                  )}
                  {tour.securityBond > 0 && (
                    <div className="tdm__fact"><span className="tdm__fact-k">Security bond</span><span className="tdm__fact-v">A${Number(tour.securityBond).toFixed(0)}</span></div>
                  )}
                </div>
              )}

              {/* Highlights */}
              {tour.highlights?.length > 0 && (
                <div className="tdm__section">
                  <h3 className="tdm__section-title">Tour highlights</h3>
                  <ul className="tdm__bullets">
                    {tour.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}

              {/* What's included */}
              {(tour.inclusions?.length > 0 || tour.includedExtra?.length > 0) && (
                <div className="tdm__section">
                  <h3 className="tdm__section-title">What's included</h3>
                  {tour.inclusions?.length > 0 && (
                    <div className="tdm__incl-grid">
                      {tour.inclusions.map(key => INCLUSION_MAP[key] && (
                        <div key={key} className="tdm__incl">
                          <span className="tdm__incl-icon">{INCLUSION_MAP[key].icon}</span>
                          <span className="tdm__incl-label">{INCLUSION_MAP[key].label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {tour.includedExtra?.length > 0 && (
                    <ul className="tdm__bullets tdm__bullets--tight">
                      {tour.includedExtra.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  )}
                </div>
              )}

              {/* Not included */}
              {tour.notIncluded?.length > 0 && (
                <div className="tdm__section">
                  <h3 className="tdm__section-title">Not included</h3>
                  <ul className="tdm__bullets tdm__bullets--muted">
                    {tour.notIncluded.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              )}

              {/* Tour/lesson information */}
              {(tour.routeNote || tour.minParticipants) && (
                <div className="tdm__section">
                  <h3 className="tdm__section-title">{isLesson ? 'Lesson information' : 'Tour information'}</h3>
                  {tour.minParticipants && <p className="tdm__info-line"><strong>Minimum riders for the tour to run:</strong> {tour.minParticipants}</p>}
                  {tour.routeNote && <p className="tdm__info-line">{tour.routeNote}</p>}
                </div>
              )}

              {/* Reviews */}
              <div className="tdm__section tdm__reviews">
                <div className="tdm__reviews-head">
                  <h3 className="tdm__section-title">Reviews</h3>
                  <div className="tdm__reviews-avg">
                    {[1, 2, 3, 4, 5].map(s => (
                      <span key={s} className={`tdm__star-display${s <= Math.round(avgRating ?? 0) ? ' tdm__star-display--on' : ''}`}>★</span>
                    ))}
                    <span className="tdm__reviews-avg-label">
                      {avgRating != null ? `${avgRating.toFixed(1)} · ${ratingCount} ${ratingCount === 1 ? 'review' : 'reviews'}` : 'No reviews yet'}
                    </span>
                  </div>
                </div>

                {/* No reviews on THIS tour yet → lean on the organizer's record
                    across their other tours, so a new listing isn't at zero. */}
                {ratingCount === 0 && organizerStats?.tourAvgRating != null && organizerStats.tourReviewCount > 0 && (
                  <div className="tdm__org-rating">
                    <span className="tdm__org-rating-stars">
                      {[1, 2, 3, 4, 5].map(s => (
                        <span key={s} className={`tdm__star-display tdm__star-display--sm${s <= Math.round(organizerStats.tourAvgRating) ? ' tdm__star-display--on' : ''}`}>★</span>
                      ))}
                    </span>
                    <span>
                      No reviews for this tour yet — <strong>{tour.userName}</strong> is rated{' '}
                      <strong>{organizerStats.tourAvgRating.toFixed(1)}</strong> from{' '}
                      {organizerStats.tourReviewCount} {organizerStats.tourReviewCount === 1 ? 'review' : 'reviews'}
                      {organizerStats.ratedTourCount > 1 ? ` across ${organizerStats.ratedTourCount} tours` : ' on their other tour'}.
                    </span>
                  </div>
                )}

                {/* Write / edit a review */}
                {canReview && (
                  <div className="tdm__review-form">
                    <span className="tdm__review-prompt">{myReview ? 'Your review' : 'Rate this tour'}</span>
                    <div className="tdm__review-stars" onMouseLeave={() => setReviewHover(0)}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <button
                          key={s}
                          type="button"
                          className={`tdm__star${s <= (reviewHover || reviewStars) ? ' tdm__star--on' : ''}`}
                          onMouseEnter={() => setReviewHover(s)}
                          onClick={() => setReviewStars(s)}
                          disabled={reviewSaving}
                          aria-label={`${s} star${s > 1 ? 's' : ''}`}
                        >★</button>
                      ))}
                    </div>
                    <textarea
                      className="tdm__review-text"
                      value={reviewText}
                      onChange={e => setReviewText(e.target.value)}
                      placeholder="Share how the ride went (optional)"
                      maxLength={600}
                      rows={3}
                    />
                    <button className="tdm__review-submit" onClick={handleSubmitReview} disabled={reviewSaving || !reviewStars}>
                      {reviewSaving ? 'Saving…' : myReview ? 'Update review' : 'Post review'}
                    </button>
                  </div>
                )}
                {!isOwner && registered && !rideDatePassed && (
                  <p className="tdm__review-locked">You can leave a review after the ride.</p>
                )}

                {/* Review list */}
                {reviews && reviews.length > 0 && (
                  <ul className="tdm__review-list">
                    {reviews.map(r => (
                      <li key={r.id} className="tdm__review-item">
                        <div className="tdm__review-item-head">
                          {r.userPhoto
                            ? <img src={r.userPhoto} alt="" className="tdm__review-avatar" />
                            : <span className="tdm__review-avatar tdm__review-avatar--initial">{r.userName?.[0]?.toUpperCase() ?? '?'}</span>}
                          <span className="tdm__review-name">{r.userName}</span>
                          <span className="tdm__review-item-stars">
                            {[1, 2, 3, 4, 5].map(s => (
                              <span key={s} className={`tdm__star-display tdm__star-display--sm${s <= r.stars ? ' tdm__star-display--on' : ''}`}>★</span>
                            ))}
                          </span>
                        </div>
                        {r.text && <p className="tdm__review-body">{r.text}</p>}
                      </li>
                    ))}
                  </ul>
                )}
                {reviews && reviews.length === 0 && !canReview
                  && !(organizerStats?.tourAvgRating != null && organizerStats.tourReviewCount > 0) && (
                  <p className="tdm__review-empty">Be the first to ride and review this tour.</p>
                )}
              </div>

              {/* Load route button. The route is gated behind registration for
                  everyone except the owner — only confirmed riders can load it. */}
              {tour.waypoints?.some(w => w.lat != null) && (() => {
                const registered = myReg?.status === 'confirmed';
                const locked = !isOwner && !registered;
                return locked ? (
                  <div className="tdm__load-locked">
                    <button className="tdm__load-btn tdm__load-btn--locked" disabled>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Load Route on Map
                    </button>
                    <span className="tdm__load-locked-note">Available after registration</span>
                  </div>
                ) : (
                  <button className="tdm__load-btn" onClick={handleLoad}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                    Load Route on Map
                  </button>
                );
              })()}

              {/* Minimum riders progress — visible to riders and organizer */}
              {showMinProgress && (
                <div className={`tdm__minriders${minMet ? ' tdm__minriders--met' : ''}`}>
                  <div className="tdm__minriders-bar">
                    <span
                      className="tdm__minriders-fill"
                      style={{ width: `${Math.min(100, Math.round((ridersToward / minRiders) * 100))}%` }}
                    />
                  </div>
                  <span className="tdm__minriders-text">
                    {minMet ? (
                      <>✓ Minimum riders reached — this tour is going ahead</>
                    ) : (
                      <>
                        <strong>{ridersToward} of {minRiders}</strong> riders needed for this tour to run
                        {recurring && minDateKey ? ` on ${fmtDateShort(minDateKey)}` : ''}
                      </>
                    )}
                  </span>
                </div>
              )}

              {/* Registration CTA */}
              {!isOwner && (
                <div className="tdm__register-box">
                  {bookingAgain && (
                    <div className="tdm__booking-again">
                      <span>Booking another date</span>
                      <button className="tdm__booking-again-back" onClick={() => setBookingAgain(false)}>
                        Back to my booking
                      </button>
                    </div>
                  )}
                  {tour.status === 'cancelled' ? (
                    <div className="tdm__full-notice">This tour has been cancelled</div>
                  ) : !user ? (
                    <div className="tdm__pro-gate">
                      <p className="tdm__pro-gate-sub">
                        {tour.registrationType === 'paid' ? 'Sign in to buy a ticket for this tour.' : 'Sign in to register for this tour.'}
                      </p>
                      <button className="tdm__register-btn" onClick={() => signIn?.()}>
                        <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1H12v2.8h5.35c-.25 1.5-1.8 4.4-5.35 4.4-3.2 0-5.8-2.65-5.8-5.9s2.6-5.9 5.8-5.9c1.8 0 3 .77 3.7 1.43l2.5-2.4C16.46 3.6 14.43 2.7 12 2.7 6.9 2.7 2.8 6.8 2.8 12s4.1 9.3 9.2 9.3c5.3 0 8.8-3.7 8.8-9 0-.6-.05-1.05-.15-1.2z"/></svg>
                        Sign in to continue
                      </button>
                    </div>
                  ) : (myReg?.status === 'confirmed' && !bookingAgain) ? (
                    <div className="tdm__registered">
                      <div className="tdm__registered-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                        {rideDatePassed
                          ? `You rode this${myRideDate ? ` on ${fmtDateShort(myRideDate)}` : ''}`
                          : `You're registered${myReg?.tourDate ? ` for ${fmtDateShort(myReg.tourDate)}` : ''}`}
                      </div>
                      {isLesson && myReg?.lessonStartTime && (
                        <div className="tdm__lesson-booked-meta">
                          {myReg.lessonStartTime} · {myReg.lessonHours}h{myReg.lessonRentBike ? ' · Bike hire' : ''}
                        </div>
                      )}
                      {/* Ticket is only useful up to the day of the ride. */}
                      {tour.status !== 'cancelled' && !rideDatePassed && (
                        <TicketQR tour={tour} uid={user.uid} registration={myReg} />
                      )}
                      {/* A ride that already happened can't be cancelled — the
                          organizer delivered it. Confirmation is a modal (see
                          bottom of file) rather than an inline expander — easy
                          to miss otherwise, same reason bookings use a modal
                          instead of a toast. */}
                      {!rideDatePassed && (
                        <button
                          className="tdm__cancel-reg-btn"
                          onClick={() => setCancelConfirm(true)}
                        >
                          Cancel registration
                        </button>
                      )}
                      {/* Recurring tours run again — let the rider book another
                          occurrence without touching this booking. Lessons are
                          exclusive one-on-one sessions with the coach, not
                          a "come back next week" repeat event, so this doesn't
                          apply there. */}
                      {!isLesson && recurring && !tourPast && tour.status !== 'cancelled' && (
                        <button className="tdm__book-again-btn" onClick={() => setBookingAgain(true)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
                          Book another date
                        </button>
                      )}
                    </div>
                  ) : tourPast ? (
                    <div className="tdm__full-notice">This tour has ended</div>
                  ) : !recurring && isFull(tour.maxParticipants, regCount) ? (
                    <div className="tdm__full-notice">This tour is full</div>
                  ) : needsCheckout ? (
                    <>
                      {dateSelectEl}
                      {lessonBookingFieldsEl}
                      {disclaimerEl}
                      <button
                        className="tdm__register-btn tdm__register-btn--paid"
                        onClick={handleBuyTicket}
                        disabled={regLoading || !acceptedDisclaimer || (recurring && (!selectedDate || selectedFull)) || (isLesson && !lessonSlotOk)}
                      >
                        {regLoading ? <span className="tdm__spinner" /> : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M2 10h20"/></svg>
                            {isLesson
                              ? `Book Lesson · $${(lessonSubtotal * (1 + PLATFORM_FEE_PCT)).toFixed(2)}`
                              : `Buy Ticket · $${(tour.ticketPrice * (1 + PLATFORM_FEE_PCT)).toFixed(2)}`}
                          </>
                        )}
                      </button>
                      <p className="tdm__fee-note">
                        {isLesson
                          ? [
                              tour.registrationType === 'paid' ? `$${lessonBaseCost.toFixed(2)} lesson` : null,
                              lessonWantsBike ? `$${lessonBikeCost.toFixed(2)} bike hire` : null,
                              `$${lessonFee.toFixed(2)} booking fee`,
                            ].filter(Boolean).join(' + ')
                          : <>${tour.ticketPrice?.toFixed(2)} ticket + ${(tour.ticketPrice * PLATFORM_FEE_PCT).toFixed(2)} booking fee</>}
                      </p>
                      {!isLesson && minRiders > 0 && !minMet && (
                        <p className="tdm__min-note">
                          This tour needs {minRiders} riders to run. If it doesn’t reach that,
                          you’ll be fully refunded — ticket and booking fee.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {dateSelectEl}
                      {lessonBookingFieldsEl}
                      {disclaimerEl}
                      <button
                      className="tdm__register-btn"
                      onClick={handleRegisterFree}
                      disabled={regLoading || !acceptedDisclaimer || (recurring && (!selectedDate || selectedFull)) || (isLesson && !lessonSlotOk)}
                    >
                      {regLoading ? <span className="tdm__spinner" /> : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                          {isLesson ? 'Book Lesson' : 'Register Free'}
                        </>
                      )}
                    </button>
                    {!isLesson && minRiders > 0 && !minMet && (
                      <p className="tdm__min-note">
                        This tour needs {minRiders} riders to run — the organizer will let you
                        know if it doesn’t go ahead.
                      </p>
                    )}
                    </>
                  )}
                </div>
              )}

              {/* Owner actions */}
              {isOwner && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {tour.status === 'cancelled' ? (
                    <>
                      <div className="tdm__full-notice">This tour is cancelled</div>
                      <button
                        className="tdm__cancel-tour-btn"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={cancelLoading}
                      >
                        {cancelLoading ? 'Deleting…' : 'Delete Tour'}
                      </button>
                    </>
                  ) : tourPast ? (
                    // Ended tours are locked down — editing or cancelling one after the
                    // fact could leave riders' refunds/details out of sync with reality.
                    <div className="tdm__full-notice">This tour has ended</div>
                  ) : (
                    <>
                      <PromoteBox tour={tour} />
                      <button
                        className="tdm__load-btn"
                        onClick={() => { onEdit?.(tour); onClose(); }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Edit Tour
                      </button>
                      <button
                        className="tdm__cancel-tour-btn"
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={cancelLoading}
                      >
                        {cancelLoading ? 'Cancelling…' : 'Cancel Tour'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Embed — only meaningful for the organizer promoting their own
                  event; a rider has no site to embed it on. */}
              {isOwner && tour.status !== 'cancelled' && (
                <div className="tdm__embed">
                  <button className="tdm__embed-toggle" onClick={() => setShowEmbed(v => !v)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                    Embed this tour on your site
                    <svg className={`tdm__embed-chevron${showEmbed ? ' tdm__embed-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showEmbed && <EmbedBuilder kind="tour" id={tour.id} />}
                </div>
              )}
            </>
          )}

          {/* ── Registrations tab (owner only) ── */}
          {activeTab === 'registrations' && isOwner && (
            <div className="tdm__regs">
              <div className="tdm__regs-header">
                <span className="tdm__regs-count">
                  {recurring ? `${todayRegs.length} registered today` : `${regCount} registered`}
                  {checkedInCount > 0 && <span className="tdm__regs-checkedin"> · {checkedInCount} checked in</span>}
                  {recurring && regCount > 0 && <span className="tdm__regs-alltime"> · {regCount} all-time</span>}
                </span>
                <div className="tdm__regs-actions">
                  {tour.status !== 'cancelled' && todayRegs.length > 0 && (
                    <button
                      className="tdm__scan-btn"
                      onClick={() => setShowScanner(true)}
                      disabled={!canCheckInToday}
                      title={canCheckInToday ? 'Scan rider tickets' : 'Check-in opens on the day of the tour'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
                      Scan check-in
                    </button>
                  )}
                  <button className="tdm__regs-refresh" onClick={loadRegistrations} title="Refresh">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                  </button>
                </div>
              </div>
              {tour.status !== 'cancelled' && todayRegs.length > 0 && !canCheckInToday && (
                <p className="tdm__checkin-hint">Check-in opens on the day of the tour.</p>
              )}

              {regsLoading && (
                <div className="tdm__regs-loading">
                  <span className="tdm__spinner tdm__spinner--dark" /> Loading…
                </div>
              )}

              {!regsLoading && recurring && (
                upcomingRegGroups.length === 0 ? (
                  <div className="tdm__regs-empty">No upcoming registrations yet</div>
                ) : (
                  <div className="tdm__reg-groups">
                    {upcomingRegGroups.map(group => {
                      const isToday = group.date === todayYMD;
                      const isOpen = expandedDates.has(group.date);
                      const left = spotsForDate(group.date);
                      const full = left != null && left <= 0;
                      const [gy, gm, gd] = group.date.split('-').map(Number);
                      const groupDate = new Date(gy, gm - 1, gd);
                      return (
                        <div key={group.date} className={`tdm__reg-group${isToday ? ' tdm__reg-group--today' : ''}`}>
                          <button className="tdm__reg-group-head" onClick={() => toggleDateExpanded(group.date)}>
                            <span className="tdm__reg-group-date">
                              {fmtDateShort(groupDate)}
                              {isToday && <span className="tdm__reg-group-badge">Today</span>}
                            </span>
                            <span className="tdm__reg-group-meta">
                              {group.regs.length} registered
                              {group.checkedIn > 0 && ` · ${group.checkedIn} in`}
                              {left != null && (full ? ' · Full' : ` · ${left} left`)}
                            </span>
                            <svg className={`tdm__reg-group-chev${isOpen ? ' tdm__reg-group-chev--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </button>
                          {isOpen && (
                            group.regs.length === 0 ? (
                              <div className="tdm__regs-empty tdm__regs-empty--nested">No registrations for this date yet</div>
                            ) : (
                              <ul className="tdm__regs-list tdm__regs-list--nested">
                                {group.regs.map((reg, i) => renderRegRow(reg, i))}
                              </ul>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {!regsLoading && !recurring && (
                regs.length === 0 ? (
                  <div className="tdm__regs-empty">No registrations yet</div>
                ) : (
                  <ul className="tdm__regs-list">
                    {regs.map((reg, i) => renderRegRow(reg, i))}
                  </ul>
                )
              )}
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
    )}

    {showScanner && (
      <Suspense fallback={null}>
        <CheckInScanner
          tourName={tour.name}
          onClose={() => setShowScanner(false)}
          onCheckedIn={loadRegistrations}
        />
      </Suspense>
    )}

    {cancelConfirm && (
      <Modal
        title="Cancel registration?"
        onClose={() => !regLoading && setCancelConfirm(false)}
        onConfirm={handleCancelRegistration}
        confirmLabel={regLoading ? 'Cancelling…' : 'Yes, cancel my spot'}
        confirmVariant="danger"
        confirmDisabled={regLoading}
      >
        <p>
          Your spot will be released and cannot be reclaimed if the tour fills up.
          {tour.registrationType === 'paid' && myReg?.paymentStatus === 'paid' && (
            cancelRefundEligible
              ? ' You’ll be refunded the ticket price — the booking fee isn’t refundable.'
              : ' This is within 48 hours of the ride, so it won’t be refunded.'
          )}
        </p>
      </Modal>
    )}

    {showDeleteConfirm && (
      <Modal
        title="Delete tour"
        onClose={() => !cancelLoading && setShowDeleteConfirm(false)}
        onConfirm={handleDeleteTour}
        confirmLabel={cancelLoading ? 'Deleting…' : 'Delete Tour'}
        confirmVariant="danger"
        confirmDisabled={cancelLoading}
      >
        <p>Permanently delete <strong>{tour.name || 'this tour'}</strong>? This cannot be undone.</p>
      </Modal>
    )}

    {showCancelConfirm && (
      <Modal
        title="Cancel tour"
        onClose={() => !cancelLoading && setShowCancelConfirm(false)}
        onConfirm={handleCancelTour}
        confirmLabel={cancelLoading ? 'Cancelling…' : 'Cancel Tour'}
        confirmVariant="danger"
        confirmDisabled={cancelLoading}
      >
        <p>
          Cancel <strong>{tour.name || 'this tour'}</strong>?{' '}
          {tour.registrationType === 'paid'
            ? 'Every rider will be fully refunded (ticket + booking fee) and emailed. This cannot be undone.'
            : 'Registered riders will be notified, and the tour will be marked cancelled.'}
        </p>
      </Modal>
    )}

    {confirmedBooking && (
      <BookingStatusModal
        variant="success"
        title={confirmedBooking.title}
        subtitle={confirmedBooking.subtitle}
        onClose={() => setConfirmedBooking(null)}
      />
    )}

    {bookingError && (
      <BookingStatusModal
        variant="error"
        subtitle={bookingError}
        onClose={() => setBookingError(null)}
      />
    )}

    {showDisclaimerTerms && <LegalModal initialTab="terms" onClose={() => setShowDisclaimerTerms(false)} />}
  </>);
}
